import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Upload, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/web`;
const err = (e, en) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : en ? "Could not complete. Please try again." : "No se pudo completar. Intente de nuevo."; };
const T = {
  es: { title: "Formulario de inicio de proyecto web", intro: "Complete la información para comenzar su página; puede guardar su avance y volver cuando quiera. Los campos con * son obligatorios.",
    sent: "Formulario enviado. Si necesita cambiar algo, escríbanos.", many: "Puede elegir varios", upload: "Subir", logo: "Logo *", photos: "Fotos del negocio", remove: "Quitar",
    save: "Guardar avance", submit: "Enviar formulario", thanks: "¡Gracias! Recibimos su información y comenzamos el diseño.", saved: "Avance guardado. Puede volver a este enlace cuando quiera." },
  en: { title: "Website project start form", intro: "Fill in the information to start your website; you can save your progress and come back anytime. Fields marked * are required.",
    sent: "Form submitted. If you need to change anything, write to us.", many: "You can choose several", upload: "Upload", logo: "Logo *", photos: "Business photos", remove: "Remove",
    save: "Save progress", submit: "Submit form", thanks: "Thank you! We received your information and are starting the design.", saved: "Progress saved. You can come back to this link anytime." },
};

function Field({ f, value, onChange, locked, t }) {
  const id = `in-${f.key}`;
  if (f.options) {
    const picked = value ? value.split(", ") : [];
    const toggle = (o) => {
      if (f.type !== "multi") return onChange(picked[0] === o ? "" : o);
      const next = picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o];
      onChange(f.options.filter((x) => next.includes(x)).join(", "));
    };
    return (
      <div className="flex flex-wrap gap-2" role={f.type === "multi" ? "group" : "radiogroup"} aria-labelledby={`${id}-l`} data-testid={`intake-${f.key}`}>
        {f.options.map((o, i) => { const on = picked.includes(o); return (
          <button key={o} type="button" disabled={locked} onClick={() => toggle(o)} aria-pressed={on}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${on ? "border-blue-700 bg-blue-700 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-blue-400"}`}
            data-testid={`intake-${f.key}-${o.replace(/\s+/g, "-").toLowerCase()}`}>{f.option_labels?.[i] || o}</button>); })}
        {f.type === "multi" && <span className="self-center text-xs text-slate-500">{t.many}</span>}
      </div>
    );
  }
  const props = { id, className: "field-input placeholder:text-slate-400", placeholder: f.placeholder, disabled: locked, value: value || "", onChange: (e) => onChange(e.target.value), "data-testid": `intake-${f.key}` };
  return f.type === "long" ? <textarea rows={f.placeholder ? 4 : 3} {...props} /> : <input {...props} />;
}

export function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white"><div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
        <img src="/logo.png" alt="RinoTECH" className="h-10 w-10" /><span className="font-display text-lg font-extrabold">RinoTECH</span></div></header>
      <main className="mx-auto max-w-3xl px-4 py-8">{children}</main>
    </div>
  );
}

function Files({ token, files, setFiles, locked, msg, setMsg, t, en }) {
  const [busy, setBusy] = useState("");
  const up = async (category, list) => {
    setMsg("");
    for (const f of list) {
      setBusy(category);
      const fd = new FormData(); fd.append("category", category); fd.append("file", f);
      try { const r = await axios.post(`${BASE}/${token}/files`, fd); setFiles((x) => [...x, r.data]); } catch (e) { setMsg(err(e, en)); }
    }
    setBusy("");
  };
  const del = async (id) => { await axios.delete(`${BASE}/${token}/files/${id}`).catch(() => {}); setFiles((x) => x.filter((f) => f.id !== id)); };
  return (
    <div className="space-y-3">
      {[["logo", t.logo, false], ["fotos", t.photos, true]].map(([c, l, multi]) => (
        <div key={c} className="rounded-md border border-dashed p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <b className="text-sm">{l}</b>
            {!locked && <label className="inline-flex cursor-pointer items-center rounded-md border px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50" data-testid={`intake-upload-${c}`}>
              {busy === c ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{t.upload}
              <input type="file" accept="image/*,application/pdf" multiple={multi} className="hidden" onChange={(e) => up(c, [...e.target.files])} data-testid={`intake-file-${c}`} />
            </label>}
          </div>
          <ul className="mt-2 space-y-1 text-sm">{files.filter((f) => f.category === c).map((f) => (
            <li key={f.id} className="flex items-center justify-between rounded bg-slate-100 px-2 py-1" data-testid={`intake-uploaded-${f.id}`}>{f.name}
              {!locked && <button type="button" onClick={() => del(f.id)} aria-label={t.remove} data-testid={`intake-remove-${f.id}`}><X className="h-4 w-4" /></button>}</li>))}</ul>
        </div>))}
      {msg && <p role="alert" className="text-sm text-rose-700" data-testid="intake-file-error">{msg}</p>}
    </div>
  );
}

export default function WebIntake() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [ans, setAns] = useState({});
  const [files, setFiles] = useState([]);
  const [state, setState] = useState({});
  const [fileMsg, setFileMsg] = useState("");
  useEffect(() => {
    axios.get(`${BASE}/${token}`).then((r) => { setInfo(r.data); setAns(r.data.answers || {}); setFiles(r.data.files || []); }).catch((e) => setState({ error: err(e) }));
  }, [token]);
  const en = info?.lang === "en";
  const t = T[en ? "en" : "es"];
  const save = async (submit) => {
    setState({ busy: submit ? "submit" : "save" });
    setFileMsg("");
    try {
      await axios.post(`${BASE}/${token}/intake`, { answers: ans, submit });
      if (submit) setInfo({ ...info, submitted_at: new Date().toISOString() });
      setState({ ok: submit ? t.thanks : t.saved });
    } catch (e) { setState({ formError: err(e, en) }); }
  };
  if (state.error) return <PublicShell><p role="alert" className="card-box p-6 text-center" data-testid="intake-error">{state.error}</p></PublicShell>;
  if (!info) return <PublicShell><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div></PublicShell>;
  const locked = Boolean(info.submitted_at);
  return (
    <PublicShell>
      <div data-testid="intake-page" lang={en ? "en" : "es"}>
        <h1 className="font-display text-3xl font-extrabold text-slate-900" data-testid="intake-title">{t.title}</h1>
        <p className="mt-2 text-slate-600">{info.project}. {t.intro}</p>
        {locked && <p className="mt-4 flex items-center gap-2 rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="intake-submitted"><CheckCircle2 className="h-5 w-5" />{t.sent}</p>}
        <form className="card-box mt-6 space-y-4 p-5" onSubmit={(e) => { e.preventDefault(); save(true); }}>
          {info.fields.map((f) => (
            <div key={f.key}>
              <label className="field-label" id={`in-${f.key}-l`} htmlFor={`in-${f.key}`}>{f.label}{f.required && " *"}</label>
              <Field f={f} t={t} value={ans[f.key]} locked={locked} onChange={(v) => setAns({ ...ans, [f.key]: v })} />
            </div>))}
          <Files token={token} t={t} en={en} files={files} setFiles={setFiles} locked={locked} msg={fileMsg} setMsg={setFileMsg} />
          {state.formError && <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="intake-form-error">{state.formError}</p>}
          {state.ok && <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="intake-ok">{state.ok}</p>}
          {!locked && <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" disabled={!!state.busy} onClick={() => save(false)} data-testid="intake-save">{t.save}</Button>
            <Button type="submit" disabled={!!state.busy} data-testid="intake-submit">{state.busy === "submit" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t.submit}</Button>
          </div>}
        </form>
      </div>
    </PublicShell>
  );
}
